import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShieldAlert, Play, Download, AlertTriangle, CheckCircle2, XCircle, Database, Loader2, StopCircle } from 'lucide-react';

// ── mock flagged rows ─────────────────────────────────────────────────────
const FLAGGED = [
  { provider: 'RunPaisa', order_id: '269412', order_invoice_id: 'HITCH683202607312246', amount: 5000, order_status: 'Failed', payment_status: '0', pg_status: 'SUCCESS', pg_txn_id: 'BANK9921', pg_date: '19 Jun 2026 08:12 PM', customer_name: 'Ramesh Kumar', a: true, b: true },
  { provider: 'PaySH', order_id: '269388', order_invoice_id: '1229942', amount: 2500, order_status: 'In Progress', payment_status: '0', pg_status: 'SUCCESS', pg_txn_id: 'UTR621921025176', pg_date: '19 Jun 2026 06:40 PM', customer_name: 'Sita Devi', a: true, b: false },
  { provider: 'RunPaisa', order_id: '269301', order_invoice_id: 'HITCH55,HITCH56', amount: 18006, order_status: 'Failed', payment_status: '0', pg_status: 'SUCCESS', pg_txn_id: 'BANK7742', pg_date: '19 Jun 2026 03:22 PM', customer_name: 'Maganbhai P.', a: true, b: true, attempts: 2 },
];

export default function TxnHighlighterPreview() {
  const [stage, setStage] = useState('processing'); // 'idle' | 'processing' | 'done'

  const loadPct = 64, verifyPct = 41;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* preview switcher */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-400 uppercase font-bold">Preview state:</span>
          {['idle', 'processing', 'done'].map(s => (
            <button key={s} onClick={() => setStage(s)}
              className={`px-3 py-1 rounded-full border ${stage === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600'}`}>{s}</button>
          ))}
        </div>

        {/* header */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center"><ShieldAlert className="w-6 h-6 text-amber-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Txn Highlighter</h1>
            <p className="text-sm text-slate-500">Pull LIVE RunPaisa / PaySH txns from the API, re-check each against the PG, and highlight money captured by the PG but shown failed/in-progress in the system.</p>
          </div>
        </div>

        {/* config + start */}
        <Card className="border border-border/60"><CardContent className="pt-5 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[11px] text-slate-500 uppercase font-bold">Provider</label>
              <Select value="all"><SelectTrigger className="w-40 h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All (RunPaisa + PaySH)</SelectItem></SelectContent></Select>
            </div>
            <div><label className="text-[11px] text-slate-500 uppercase font-bold">From</label><Input type="date" defaultValue="2026-06-15" className="h-9 mt-1 w-40" /></div>
            <div><label className="text-[11px] text-slate-500 uppercase font-bold">To</label><Input type="date" defaultValue="2026-06-19" className="h-9 mt-1 w-40" /></div>
            {stage === 'processing'
              ? <Button size="sm" disabled className="h-9 gap-1.5 bg-amber-600 text-white"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing…</Button>
              : <Button size="sm" className="h-9 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"><Play className="w-3.5 h-3.5" /> Start processing</Button>}
            {stage === 'done' && <Button variant="ghost" size="sm" className="h-9 gap-1.5"><Download className="w-3.5 h-3.5" /> Export highlights</Button>}
          </div>
          <p className="text-xs text-slate-400">Heavy job — the app fetches every txn page live for the range, so this can take a few minutes. You can leave this tab and come back; progress keeps running.</p>
        </CardContent></Card>

        {/* PROCESSING PANEL */}
        {stage !== 'idle' && (
          <Card className="border border-amber-200 bg-amber-50/40"><CardContent className="pt-5 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                {stage === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
                {stage === 'done' ? 'Processing complete' : 'Processing — 15 Jun → 19 Jun 2026'}
              </div>
              {stage === 'processing' && <Button variant="outline" size="sm" className="h-8 gap-1.5 text-red-600 border-red-200"><StopCircle className="w-3.5 h-3.5" /> Cancel</Button>}
            </div>

            {/* Phase 1 */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 font-medium text-slate-600"><Database className="w-3.5 h-3.5" /> Step 1 · Loading transactions from API</span>
                <span className="text-slate-500">{stage === 'done' ? '110 / 110 pages · 2,184 records' : '70 / 110 pages · 1,392 records loaded'}</span>
              </div>
              <Progress value={stage === 'done' ? 100 : loadPct} className="h-2" />
            </div>

            {/* Phase 2 */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 font-medium text-slate-600"><ShieldAlert className="w-3.5 h-3.5" /> Step 2 · Verifying PG status of failed / in-progress txns</span>
                <span className="text-slate-500">{stage === 'done' ? '188 / 188 verified' : '77 / 188 verified'}</span>
              </div>
              <Progress value={stage === 'done' ? 100 : verifyPct} className="h-2 [&>div]:bg-amber-500" />
            </div>
          </CardContent></Card>
        )}

        {/* SUMMARY STATS */}
        {stage !== 'idle' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Candidates (failed / in-progress)" value="188" color="text-slate-800" />
            <Stat label="Verified so far" value={stage === 'done' ? '188' : '77'} color="text-blue-600" />
            <Stat label="Success in PG · Not in System" value="3" color="text-red-600" icon={<AlertTriangle className="w-4 h-4" />} />
            <Stat label="Order Failed · Payment Success" value="2" color="text-rose-700" icon={<XCircle className="w-4 h-4" />} />
          </div>
        )}

        {/* RESULTS */}
        {stage !== 'idle' && (
          <Card className="border border-border/60"><CardContent className="p-0 overflow-x-auto">
            <div className="px-4 py-3 border-b border-border/40 flex items-center gap-3 text-sm">
              <span className="font-semibold text-slate-700">Highlighted mismatches</span>
              <Badge className="bg-red-100 text-red-700 border-red-200">Success in PG, not in System</Badge>
              <Badge className="bg-rose-100 text-rose-700 border-rose-200">Order failed, payment success</Badge>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Provider</th>
                  <th className="text-left px-3 py-2 font-bold">Invoice / Order ID</th>
                  <th className="text-left px-3 py-2 font-bold">Amount</th>
                  <th className="text-left px-3 py-2 font-bold">System status</th>
                  <th className="text-left px-3 py-2 font-bold">PG status</th>
                  <th className="text-left px-3 py-2 font-bold">Highlight</th>
                  <th className="text-left px-3 py-2 font-bold">PG txn / date</th>
                  <th className="text-left px-3 py-2 font-bold">Customer</th>
                </tr>
              </thead>
              <tbody>
                {FLAGGED.map((r, i) => (
                  <tr key={i} className={`border-b border-border/20 ${r.b ? 'bg-rose-50' : 'bg-red-50/60'}`}>
                    <td className="px-3 py-2.5 font-medium">{r.provider}</td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">{r.attempts ? <span>{r.order_invoice_id.split(',')[0]}… <span className="font-sans text-amber-600 font-semibold">+{r.attempts - 1}</span></span> : r.order_invoice_id}<div className="text-slate-400">#{r.order_id}</div></td>
                    <td className="px-3 py-2.5 font-semibold">₹{r.amount.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2.5"><span className="text-slate-600">{r.order_status}</span><span className="text-slate-400"> · ps={r.payment_status}</span></td>
                    <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" />{r.pg_status}</Badge></td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col gap-1">
                        {r.a && <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200 w-fit">PG success · not in system</Badge>}
                        {r.b && <Badge className="text-[9px] bg-rose-100 text-rose-700 border-rose-200 w-fit">Order failed · paid</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">{r.pg_txn_id}<div className="text-slate-400">{r.pg_date}</div></td>
                    <td className="px-3 py-2.5 text-slate-600">{r.customer_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}

        {stage === 'idle' && (
          <Card className="border border-dashed border-border/60"><CardContent className="py-16 text-center text-sm text-slate-400">
            Pick a provider + date range and hit <span className="font-semibold text-amber-600">Start processing</span> to fetch live txns and highlight PG mismatches.
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, color, icon }) => (
  <div className="rounded-xl border border-border/60 bg-white px-4 py-3 text-center">
    <div className={`text-2xl font-bold flex items-center justify-center gap-1.5 ${color}`}>{icon}{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{label}</div>
  </div>
);
